import React, { useMemo, useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLazyGetPaymentReceiptQuery } from '../../../api/endpoints/creditCardPayment';
import PaymentReceiptPdfForm from '../paymentReports/utils/PaymentReceiptPdfForm';
import { toastService } from '../../../utils/toastService';

/**
 * Tutar string'ini sayıya çevirir
 * "200000,00" veya "200000.00" formatını destekler
 */
const parseAmount = (amountStr) => {
    if (!amountStr) return null;
    // Virgülü noktaya çevir (Türkçe format için)
    const normalized = amountStr.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
};

const PaymentResult = () => {
    const { th, language } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [getPaymentReceipt, { isLoading: isLoadingReceipt }] = useLazyGetPaymentReceiptQuery();

    // URL parametrelerini al
    const paymentInfo = useMemo(() => ({
        success: searchParams.get('success') === 'true',
        transactionId: searchParams.get('transactionId'),
        paymentGuid: searchParams.get('paymentGuid'),
        referenceCode: searchParams.get('referenceCode'),
        authCode: searchParams.get('authCode'),
        amount: parseAmount(searchParams.get('amount')),
        error: searchParams.get('error'),
        // Yeni alanlar
        paymentType: searchParams.get('paymentType'),
        bankName: searchParams.get('bankName'),
        currency: searchParams.get('currency') || 'TRY',
        installmentCount: searchParams.get('installmentCount')
    }), [searchParams]);

    // Ödeme tipi formatı
    const formattedPaymentType = useMemo(() => {
        if (paymentInfo.paymentType) {
            return paymentInfo.paymentType;
        }
        if (paymentInfo.installmentCount) {
            const count = parseInt(paymentInfo.installmentCount, 10);
            if (count > 1) {
                return `${count} Taksit`;
            }
        }
        return th('creditCardCollection.labels.singlePayment') || 'Tek Çekim';
    }, [paymentInfo.paymentType, paymentInfo.installmentCount, th]);

    // Para birimi sembolü
    const currencySymbol = paymentInfo.currency === 'TRY' ? '₺' : paymentInfo.currency;

    // Yeni ödeme sayfasına git
    const handleNewPayment = () => {
        navigate('/parts/credit-card-collection');
    };

    // Ana sayfaya git
    const handleGoHome = () => {
        navigate('/');
    };

    // Makbuz indirme
    const handleDownloadReceipt = useCallback(async () => {
        if (!paymentInfo.transactionId) {
            toastService.error(th('paymentReports.errors.receiptNotFound') || 'Makbuz bulunamadı');
            return;
        }

        try {
            const result = await getPaymentReceipt(paymentInfo.transactionId).unwrap();
            
            if (result.success && result.data) {
                // PDF için çevirileri hazırla
                const receiptTranslations = {
                    title: th('paymentReports.receipt.title'),
                    customerName: th('paymentReports.receipt.customerName'),
                    customerNo: th('paymentReports.receipt.customerNo'),
                    paymentType: th('paymentReports.receipt.paymentType'),
                    collectionAmount: th('paymentReports.receipt.collectionAmount'),
                    transactionDate: th('paymentReports.receipt.transactionDate'),
                    bankApprovalCode: th('paymentReports.receipt.bankApprovalCode'),
                    bank: th('paymentReports.receipt.bank'),
                    address: th('paymentReports.receipt.address'),
                    phone: th('paymentReports.receipt.phone'),
                    fax: th('paymentReports.receipt.fax'),
                    legalTextPart1: th('paymentReports.receipt.legalTextPart1'),
                    legalTextPart2: th('paymentReports.receipt.legalTextPart2'),
                    inWords: th('paymentReports.receipt.inWords'),
                    refundFormTitle: th('paymentReports.receipt.refundFormTitle'),
                    refundFormText: th('paymentReports.receipt.refundFormText'),
                    phoneNo: th('paymentReports.receipt.phoneNo'),
                    authorizedSignature: th('paymentReports.receipt.authorizedSignature')
                };

                // PDF oluştur ve indir
                const pdfForm = new PaymentReceiptPdfForm();
                const pdfDoc = pdfForm.generate(result.data, receiptTranslations, language);
                
                const fileName = `Makbuz_${paymentInfo.transactionId}.pdf`;
                pdfDoc.download(fileName);
            } else {
                toastService.error(th('paymentReports.errors.receiptNotFound') || 'Makbuz bulunamadı');
            }
        } catch (error) {
            console.error('Error fetching receipt:', error);
            toastService.error(th('paymentReports.errors.receiptFetchError') || 'Makbuz alınırken hata oluştu');
        }
    }, [paymentInfo.transactionId, getPaymentReceipt, th, language]);

    // Başarılı durumu
    if (paymentInfo.success) {
        return (
            <div className="flex align-items-start justify-content-center min-h-screen surface-ground pt-6">
                <Card className="w-full md:w-6 lg:w-4 shadow-4">
                    <div className="text-center">
                        {/* Başarı İkonu */}
                        <div className="mb-4">
                            <i 
                                className="pi pi-check-circle text-green-500" 
                                style={{ fontSize: '5rem' }}
                            />
                        </div>

                        {/* Başlık */}
                        <h1 className="text-3xl font-bold text-green-600 mb-3">
                            {th('creditCardCollection.success.title') || 'Ödeme Başarılı!'}
                        </h1>

                        {/* Alt Başlık */}
                        <p className="text-xl text-600 mb-5">
                            {th('creditCardCollection.success.subtitle') || 'Ödemeniz başarıyla tamamlandı.'}
                        </p>

                        {/* Ödeme Bilgileri */}
                        <div className="surface-100 border-round-xl p-4 mb-5 text-left">
                            {paymentInfo.transactionId && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.transactionId') || 'İşlem No'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {paymentInfo.transactionId}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.referenceCode && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.referenceCode') || 'Referans Kodu'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {paymentInfo.referenceCode}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.authCode && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.authCode') || 'Onay Kodu'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {paymentInfo.authCode}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.bankName && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.bankName') || 'Banka'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {paymentInfo.bankName}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.paymentType && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.paymentType') || 'Ödeme Tipi'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {paymentInfo.paymentType}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.installmentCount && (
                                <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.installmentCount') || 'Taksit'}
                                    </span>
                                    <span className="font-semibold text-900">
                                        {parseInt(paymentInfo.installmentCount, 10) > 1 
                                            ? `${paymentInfo.installmentCount} ${th('creditCardCollection.labels.installment') || 'Taksit'}`
                                            : th('creditCardCollection.labels.singlePayment') || 'Tek Çekim'}
                                    </span>
                                </div>
                            )}

                            {paymentInfo.amount && (
                                <div className="flex justify-content-between align-items-center py-2">
                                    <span className="text-600">
                                        {th('creditCardCollection.labels.amount') || 'Tutar'}
                                    </span>
                                    <span className="font-bold text-xl text-green-600">
                                        {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(paymentInfo.amount)} {currencySymbol}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Butonlar */}
                        <div className="flex flex-column md:flex-row gap-3 justify-content-center">
                            <Button
                                label={th('creditCardCollection.buttons.downloadReceipt') || 'Makbuz İndir'}
                                icon="pi pi-download"
                                className="p-button-info"
                                onClick={handleDownloadReceipt}
                                loading={isLoadingReceipt}
                                disabled={!paymentInfo.transactionId}
                            />
                            <Button
                                label={th('creditCardCollection.buttons.newPayment') || 'Yeni Ödeme'}
                                icon="pi pi-plus"
                                className="p-button-success"
                                onClick={handleNewPayment}
                            />
                            <Button
                                label={th('creditCardCollection.buttons.goHome') || 'Ana Sayfaya Dön'}
                                icon="pi pi-home"
                                className="p-button-outlined"
                                onClick={handleGoHome}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    // Hata durumu (success=false)
    const errorMessage = paymentInfo.error 
        ? decodeURIComponent(paymentInfo.error) 
        : (th('creditCardCollection.errors.threeDFailed') || '3D doğrulama başarısız');

    return (
        <div className="flex align-items-start justify-content-center min-h-screen surface-ground pt-6">
            <Card className="w-full md:w-6 lg:w-4 shadow-4">
                <div className="text-center">
                    {/* Hata İkonu */}
                    <div className="mb-4">
                        <i 
                            className="pi pi-times-circle text-red-500" 
                            style={{ fontSize: '5rem' }}
                        />
                    </div>

                    {/* Başlık */}
                    <h1 className="text-3xl font-bold text-red-600 mb-3">
                        {th('creditCardCollection.failure.title') || 'Ödeme Başarısız'}
                    </h1>

                    {/* Alt Başlık */}
                    <p className="text-xl text-600 mb-5">
                        {th('creditCardCollection.failure.subtitle') || 'Ödeme işlemi tamamlanamadı.'}
                    </p>

                    {/* Hata Bilgileri */}
                    <div className="surface-100 border-round-xl p-4 mb-5 text-left">
                        <div className="flex flex-column py-2 border-bottom-1 surface-border">
                            <span className="text-600 mb-1">
                                {th('creditCardCollection.labels.errorMessage') || 'Hata Mesajı'}
                            </span>
                            <span className="font-semibold text-red-600">
                                {errorMessage}
                            </span>
                        </div>

                        {paymentInfo.transactionId && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.transactionId') || 'İşlem No'}
                                </span>
                                <span className="font-semibold text-900">
                                    {paymentInfo.transactionId}
                                </span>
                            </div>
                        )}

                        {paymentInfo.bankName && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.bankName') || 'Banka'}
                                </span>
                                <span className="font-semibold text-900">
                                    {paymentInfo.bankName}
                                </span>
                            </div>
                        )}

                        {(paymentInfo.paymentType || paymentInfo.installmentCount) && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.paymentType') || 'Ödeme Tipi'}
                                </span>
                                <span className="font-semibold text-900">
                                    {formattedPaymentType}
                                </span>
                            </div>
                        )}

                        {paymentInfo.amount && (
                            <div className="flex justify-content-between align-items-center py-2">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.amount') || 'Tutar'}
                                </span>
                                <span className="font-bold text-xl text-900">
                                    {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(paymentInfo.amount)} {currencySymbol}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Bilgi Mesajı */}
                    <div className="bg-orange-50 border-round-xl p-3 mb-5">
                        <i className="pi pi-exclamation-triangle text-orange-500 mr-2" />
                        <span className="text-orange-700">
                            {th('creditCardCollection.failure.info') || 'Lütfen kart bilgilerinizi kontrol edip tekrar deneyin veya farklı bir kart kullanın.'}
                        </span>
                    </div>

                    {/* Butonlar */}
                    <div className="flex flex-column md:flex-row gap-3 justify-content-center">
                        <Button
                            label={th('creditCardCollection.buttons.retry') || 'Tekrar Dene'}
                            icon="pi pi-refresh"
                            className="p-button-warning"
                            onClick={handleNewPayment}
                        />
                        <Button
                            label={th('creditCardCollection.buttons.goHome') || 'Ana Sayfaya Dön'}
                            icon="pi pi-home"
                            className="p-button-outlined"
                            onClick={handleGoHome}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PaymentResult;
